/**
 * LabsSuiteHeroBand — PQC Audit Suite headline band for `/labs` (Iter269).
 *
 * Repositions the Labs landing page to lead with the moat-deepener
 * audit family before the rest of the engineering services. Pulls
 * live pricing from `/api/public/pricing/audits/bundle` so the math
 * never drifts from the actual SKU catalog.
 *
 * Renders:
 *   • Eyebrow + 4-line headline
 *   • Live bundle pricing strip: "$15k + $12k + $14k → save up to $10,250"
 *   • 3 audit chips with NEW pills, each linking to its detail page
 *   • Primary CTA → /pricing/audits#pqc-suite (the configurator)
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Atom, Cpu, Network, ArrowRight, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


export default function LabsSuiteHeroBand() {
  const [catalog, setCatalog] = useState(null);
  const [bestQuote, setBestQuote] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cat, q] = await Promise.all([
          axios.get(`${API}/public/pricing/audits/bundle`),
          axios.get(`${API}/public/pricing/audits/bundle-quote`, {
            params: {
              slugs: [
                "hybrid-signature-transition-audit",
                "eip-7702-aa-pqc-audit",
                "mpc-wallet-pqc-compatibility-checker",
              ].join(","),
              tier: "standard",
            },
          }),
        ]);
        if (cancelled) return;
        setCatalog(cat.data);
        setBestQuote(q.data);
      } catch {
        // Backend may be cold or unreachable — fail silently so the
        // band just doesn't render rather than breaking /labs.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!catalog || !bestQuote) return null;

  const ICONS = {
    "hybrid-signature-transition-audit":      Atom,
    "eip-7702-aa-pqc-audit":                  Cpu,
    "mpc-wallet-pqc-compatibility-checker":   Network,
  };

  return (
    <section className="max-w-[1400px] mx-auto px-6 sm:px-8 py-12"
             data-testid="labs-suite-hero-band">
      <div className="qt-card relative overflow-hidden"
           style={{
             border: "1px solid rgba(31,221,131,0.42)",
             background:
               "radial-gradient(ellipse at top right, rgba(31,221,131,0.10), transparent 65%), " +
               "linear-gradient(135deg, rgba(0,229,255,0.05), transparent 65%)",
             padding: "2rem 2rem 1.75rem",
           }}>
        <div className="qt-mono text-xs"
             style={{ color: "#1FDD83", letterSpacing: "0.20em", fontWeight: 700 }}
             data-testid="labs-suite-eyebrow">
          <Sparkles size={12} className="inline mr-1.5" />
          QORTRACE PQC AUDIT SUITE · LABS HEADLINE PRODUCT
        </div>

        <h2 className="qt-display text-3xl sm:text-4xl lg:text-5xl mt-3"
            style={{ color: "#fff", lineHeight: 1.08 }}
            data-testid="labs-suite-headline">
          The three PQC audits that{" "}
          <span style={{ color: "#1FDD83" }}>don't exist anywhere else</span>,
          bundled.
        </h2>
        <p className="mt-4 max-w-3xl text-base sm:text-lg"
           style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
          Hybrid Signature. EIP-7702 / Account Abstraction. MPC Wallet
          PQC. Three signed audit engagements, each scoped to a
          cryptographic surface no generalist firm covers. Bundle any
          two and save 15%. All three — save 25%.
        </p>

        {/* Pricing strip */}
        <div className="mt-7 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-5 items-end">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3"
               data-testid="labs-suite-audit-chips">
            {catalog.audits.map((a) => {
              const Icon = ICONS[a.slug] || Atom;
              return (
                <Link key={a.slug}
                      to={`/labs/services/${a.slug}`}
                      data-testid={`labs-suite-chip-${a.slug}`}
                      className="flex flex-col gap-1.5 transition-all"
                      style={{
                        border: "1px solid var(--qt-border)",
                        background: "rgba(7,10,18,0.65)",
                        borderRadius: 10,
                        padding: "1rem 1.1rem",
                        textDecoration: "none",
                      }}>
                  <div className="flex items-center gap-2">
                    <Icon size={15} style={{ color: "#1FDD83" }} />
                    <span className="qt-mono text-[10px]"
                          style={{ color: "#1FDD83", letterSpacing: "0.16em", fontWeight: 700 }}>
                      NEW · PQC AUDIT
                    </span>
                  </div>
                  <div className="qt-display text-base"
                       style={{ color: "#fff", lineHeight: 1.2 }}>
                    {a.title}
                  </div>
                  <div className="qt-mono text-xs mt-1"
                       style={{ color: "var(--qt-text-3)" }}>
                    from ${a.prices_usd.standard.toLocaleString()}
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Quote summary */}
          <div className="flex flex-col items-end text-right"
               data-testid="labs-suite-quote-strip">
            <div className="qt-mono text-[10px]"
                 style={{ color: "var(--qt-text-3)", letterSpacing: "0.16em" }}>
              FULL SUITE · STANDARD TIER
            </div>
            <div className="mt-1 flex items-baseline gap-2 justify-end">
              <span className="qt-mono text-sm line-through"
                    style={{ color: "var(--qt-text-3)" }}>
                ${bestQuote.subtotal_usd.toLocaleString()}
              </span>
              <span className="qt-display text-3xl"
                    style={{ color: "#1FDD83" }}>
                ${bestQuote.total_usd.toLocaleString()}
              </span>
            </div>
            <div className="qt-mono text-[10px] mt-1"
                 style={{ color: "#1FDD83", letterSpacing: "0.12em", fontWeight: 700 }}>
              SAVE ${bestQuote.discount_amount_usd.toLocaleString()} · {bestQuote.discount_pct}% OFF
            </div>

            <Link to="/pricing/enterprise#pqc-suite"
                  data-testid="labs-suite-cta"
                  className="qt-btn qt-btn-primary inline-flex mt-4"
                  style={{
                    fontSize: 13, padding: "0.7rem 1.1rem",
                    background: "#1FDD83", color: "#04060a",
                    fontWeight: 700,
                  }}>
              Configure your suite <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
